/**
 * Cotacao, pedido e pagamento do cliente — `API §6.10`, `§6.12`, `§6.13`.
 *
 * As tres rotas de escrita daqui sao as que a tabela do `API §3.1` nomeia primeiro: *"`POST
 * /v1/orders` — pedido duplicado"*, *"`POST /v1/orders/{id}/payments` — cobranca duplicada"*,
 * *"`POST /v1/me/orders/{id}/cancel` — estorno duplicado"*. Todas levam `Idempotency-Key`, e o
 * escopo de cada uma e a **intencao**, nao a requisicao:
 *
 * | Rota | Escopo | Por que |
 * | ---- | ------ | ------- |
 * | `POST /v1/orders` | `POST /v1/orders:<quoteId>` | Duas cotacoes diferentes sao dois pedidos diferentes; dois toques na mesma cotacao sao um so |
 * | `POST /v1/orders/{id}/payments` | o caminho (ja tem o pedido) | Uma cobranca por pedido de cada vez |
 * | `POST /v1/me/orders/{id}/cancel` | o caminho | Um estorno por pedido |
 *
 * **A cotacao (`POST /v1/checkout/quote`) NAO leva chave** — ela nao esta na tabela do §3.1, e
 * nao esta porque cotar nao produz efeito: repetir cotacao gera outra cotacao, o que e o
 * comportamento desejado quando o preco mudou.
 */
import {
  MyOrdersResponseSchema,
  OrderCreatedResponseSchema,
  OrderDetailSchema,
  PaymentResponseSchema,
  QuoteResponseSchema,
  type CancelOrderRequest,
  type CreateOrderRequest,
  type CreatePaymentRequest,
  type MyOrdersQuerySchema,
  type MyOrdersResponse,
  type OrderCreatedResponse,
  type OrderDetail,
  type PaymentResponse,
  type QuoteRequest,
  type QuoteResponse,
} from '@farmacia/contracts';
import type { z } from 'zod';

import type { ApiClientCore } from '../core.js';

export type MyOrdersInput = z.input<typeof MyOrdersQuerySchema>;

export interface CheckoutApi {
  /**
   * `POST /v1/checkout/quote` — o **unico** lugar do contrato publico onde o dinheiro do
   * pedido aparece, e sempre como saida. TTL de 10 min (`QUOTE_TTL_MINUTES`).
   */
  quote: (body: QuoteRequest, signal?: AbortSignal) => Promise<QuoteResponse>;
}

export interface OrdersApi {
  /**
   * `POST /v1/orders`. `201` no caminho normal; **`200`** quando a trava de negocio devolveu o
   * pedido que ja existia (`API §3.2`, `meta.deduplicatedBy: BUSINESS_RULE`). O `status` da
   * resposta e devolvido junto porque a distincao muda a tela: "criamos" e "ja estava criado".
   */
  create: (body: CreateOrderRequest) => Promise<{
    readonly order: OrderCreatedResponse;
    readonly created: boolean;
    /** `true` se veio do replay da chave de idempotencia (`Idempotent-Replayed`). */
    readonly replayed: boolean;
  }>;
  mine: (query?: MyOrdersInput, signal?: AbortSignal) => Promise<MyOrdersResponse>;
  byId: (orderId: string, signal?: AbortSignal) => Promise<OrderDetail>;
  cancel: (orderId: string, body?: CancelOrderRequest) => Promise<OrderDetail>;
  /** `POST /v1/orders/{id}/payments`. Pix por porta ou pagamento na entrega. */
  pay: (orderId: string, body: CreatePaymentRequest) => Promise<PaymentResponse>;
}

export function createCheckoutApi(core: ApiClientCore): CheckoutApi {
  return {
    async quote(body, signal) {
      const { data } = await core.request({
        method: 'POST',
        path: '/v1/checkout/quote',
        body,
        response: QuoteResponseSchema,
        ...(signal === undefined ? {} : { signal }),
      });
      return data;
    },
  };
}

export function createOrdersApi(core: ApiClientCore): OrdersApi {
  return {
    async create(body) {
      const response = await core.request({
        method: 'POST',
        path: '/v1/orders',
        body,
        response: OrderCreatedResponseSchema,
        // A intencao e a cotacao. Dois toques no mesmo botao reusam a chave; um pedido novo,
        // com cotacao nova, ganha chave nova sem que a tela precise pedir.
        idempotency: `POST /v1/orders:${body.quoteId}`,
      });
      return {
        order: response.data,
        created: response.status === 201,
        replayed: response.replayed,
      };
    },

    async mine(query, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: '/v1/me/orders',
        ...(query === undefined ? {} : { query: { ...query } }),
        response: MyOrdersResponseSchema,
        ...(signal === undefined ? {} : { signal }),
      });
      return data;
    },

    async byId(orderId, signal) {
      const { data } = await core.request({
        method: 'GET',
        path: `/v1/me/orders/${encodeURIComponent(orderId)}`,
        response: OrderDetailSchema,
        ...(signal === undefined ? {} : { signal }),
      });
      return data;
    },

    async cancel(orderId, body) {
      const { data } = await core.request({
        method: 'POST',
        path: `/v1/me/orders/${encodeURIComponent(orderId)}/cancel`,
        body: body ?? {},
        response: OrderDetailSchema,
        idempotency: true,
      });
      return data;
    },

    async pay(orderId, body) {
      const { data } = await core.request({
        method: 'POST',
        path: `/v1/orders/${encodeURIComponent(orderId)}/payments`,
        body,
        response: PaymentResponseSchema,
        idempotency: true,
      });
      return data;
    },
  };
}
